/** @jsxImportSource @emotion/react */

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Input, message } from "antd";
import { RegisterFormType } from "api/auth/type";
import { ReactComponent as StarIcon } from "assets/common/icons/star-solid.svg";
import { ModalLogo } from "assets/common/modal";
import CheckboxCustom from "components/common/CheckBoxCustom";
import HelmetCustom from "components/common/HelmetCustom";
import InputCustomAuth from "components/common/InputCustomAuth";
import { InputCustomStyle } from "components/common/InputCustomAuth/index.style";
import ModalNotiMobile from "components/common/Modal/ModalNotiMobile";
import { LIST_BACKGROUND_CAPTCHA } from "constants/captcha";
import { PATH } from "constants/path";
import {
  REGEX_EMAIL,
  REGEX_EMOJI,
  REGEX_NUMBER,
  REGEX_PASSWORD,
  REGEX_USERNAME,
} from "constants/regex";
import { ResponseCode } from "constants/response";
import { ModalType } from "enums/modalType";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { selectIsAuthenticated } from "store/auth";
import { registerMemberDetail } from "store/auth/auth.thunk";
import { setOpenModalNotiMobile } from "store/master";
import { ApiResponse } from "types/api";
import * as yup from "yup";

import { SignupPageStyle } from "./index.style";

interface RegisterType extends RegisterFormType {
  approve: boolean;
}

interface State {
  valueCaptcha: string;
  numberBg: number;
  title: string;
}
const schema = yup
  .object({
    mobile: yup.string().trim().required("Đây là trường bắt buộc"),
    fullname: yup.string().trim().required("Đây là trường bắt buộc"),
    username: yup
      .string()
      .trim()
      .lowercase()
      .max(16, "Độ dài tối đa 16 ký tự")
      .min(6, "Độ dài tối thiểu 6 ký tự")
      .required("Đây là trường bắt buộc")
      .matches(REGEX_USERNAME, `Tài khoản sai định dạng.`)
      .required("Vui lòng nhập tài khoản")
      .test("emoji", "Không nhập emoji", (username: string): boolean => {
        if (!username) return false;
        const regex = new RegExp(REGEX_EMOJI);
        return !regex.test(username);
      }),
    password: yup
      .string()
      .trim()
      .max(16, "Độ dài tối đa 16 ký tự")
      .min(6, "Độ dài tối thiểu 6 ký tự")
      .matches(REGEX_PASSWORD, "Hơn 6 ký tự phải bao gồm chữ cái và số")
      .required("Đây là trường bắt buộc"),

    confirm_password: yup
      .string()
      .trim()
      .max(16, "Độ dài tối đa 16 ký tự")
      .min(6, "Độ dài tối thiểu 6 ký tự")
      .matches(REGEX_PASSWORD, "Hơn 6 ký tự phải bao gồm chữ cái và số")
      .required("Đây là trường bắt buộc")
      .test(
        "empty",
        "Xác nhận mật khẩu không khớp",
        (
          confirm_password: string | undefined,
          testContext: yup.TestContext<yup.AnyObject>,
        ): boolean => {
          if (!testContext.parent.password) return true;
          return testContext.parent.password === confirm_password;
        },
      ),
    // email: yup
    //   .string()
    //   .email("Invalid email")
    //   .trim()
    //   .required("Đây là trường bắt buộc"),
    approve: yup
      .boolean()
      .required()
      .oneOf([true], "Bạn phải đủ 18 tuổi để sử dụng trang web này."),
    captcha: yup
      .string()
      .trim()
      .required("mã xác nhận không được bỏ trống, xin lấy mã xác nhận"),
  })
  .required();

const SignupPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fullNameValue, setFullName] = useState<string>("");

  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");

  const [state, setState] = useState<State>({
    valueCaptcha: "",
    numberBg: 0,
    title: "",
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm<RegisterType>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      fullname: "",
      email: "",
    },
  });

  const onSubmit = async (data: RegisterType) => {
    // eslint-disable-next-line prefer-const
    let isCheckEmail = false;

    const regex = new RegExp(REGEX_EMAIL);

    if (data.email) {
      if (!regex.test(data.email)) {
        setState((prev) => ({
          ...prev,
          title: "Vui lòng nhập email đúng định dạng!",
        }));
        isCheckEmail = true;
      }
    }

    if (isCheckEmail) return;

    setState((prev) => ({
      ...prev,
      title: "",
    }));

    const res = await dispatch(
      registerMemberDetail({
        ...data,
        fullname: fullNameValue,
        referral: code || "",
      }),
    );
    const resData = res.payload as ApiResponse;
    if (resData.status !== ResponseCode.SUCCESS) {
      message.error({
        key: "register",
        content: resData.msg,
      });
      return;
    }
    message.success({
      key: "register",
      content: resData.msg,
    });
    reset();
    navigate("/");
  };

  useEffect(() => {
    generateRandomNumbers();

    if (!isAuthenticated) return;

    navigate("/");
  }, []);

  const generateRandomNumbers = () => {
    const newRandomNumbers: number[] = [];

    while (newRandomNumbers.length < 4) {
      const randomNumber = Math.floor(Math.random() * 10);
      newRandomNumbers.push(randomNumber);
    }

    setState((prev) => ({
      ...prev,
      numberBg: Math.floor(Math.random() * 9),
      valueCaptcha: newRandomNumbers.join(""),
    }));
  };

  const renderTitle = () => {
    const form = getValues();

    if (!form.captcha) {
      setState((prev) => ({
        ...prev,
        title: "Lỗi mã xác minh hoặc lỗi đầu vào, vui lòng quay lại",
      }));
      return;
    }

    if (errors.captcha || !form.captcha) {
      setState((prev) => ({
        ...prev,
        title: errors.captcha?.message as string,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      title: "",
    }));
  };

  const handleInputChange = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;

    if (!REGEX_NUMBER.test(key) && key !== "Enter" && key !== "Backspace") {
      event.preventDefault();
    }
  };

  return (
    <div css={SignupPageStyle.wrapper}>
      <HelmetCustom title="Signup" />
      <div css={SignupPageStyle.header}>
        <Link to={PATH.Home} css={SignupPageStyle.btnClose}>
          ✖
        </Link>
        <div css={SignupPageStyle.headerLogo}>
          <img src={ModalLogo} alt="" />
        </div>
      </div>

      <Form css={SignupPageStyle.form} onSubmitCapture={handleSubmit(onSubmit)}>
        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Tên tài khoản</p>
        </div>
        <InputCustomAuth
          placeholder={"Vui lòng nhập tên tài khoản"}
          name={"username"}
          control={control}
          margin_bottom="12px"
          id={"username"}
          errors={errors.username?.message}
          validate_status={errors.username ? "error" : "success"}
          isPrefix
        />
        <p css={SignupPageStyle.textRequired}>
          Phải lớn hơn 6 ký tự, bao gồm cả chữ và số, phải bắt đầu bằng chữ cái,
          không điền các ký tự đặc biệt.
        </p>

        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Mật khẩu thành viên</p>
        </div>

        <InputCustomAuth
          placeholder={"Vui lòng nhập mật khẩu thành viên"}
          type="password"
          name={"password"}
          control={control}
          margin_bottom="12px"
          id={"password"}
          errors={errors.password?.message}
          validate_status={errors.password ? "error" : "success"}
          isPrefix
        />
        <p css={SignupPageStyle.textRequired}>
          Hơn 6 Ký Tự Phải Bao Gồm Chữ Cái Và Số
        </p>

        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Xác nhận mật khẩu</p>
        </div>
        <InputCustomAuth
          placeholder={"Vui lòng nhập mật khẩu thành viên"}
          type="password"
          name={"confirm_password"}
          control={control}
          margin_bottom="12px"
          id={"confirm_password"}
          errors={errors.confirm_password?.message}
          validate_status={errors.confirm_password ? "error" : "success"}
          isPrefix
        />
        <p css={SignupPageStyle.textRequired}>
          Vui lòng xác nhận lại mật khẩu của bạn
        </p>

        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Tên thật</p>
        </div>
        <Form.Item
          css={InputCustomStyle.self(true)}
          help={errors.fullname?.message}
          validateStatus={errors.fullname ? "error" : "success"}
          className="input-custom"
          style={{
            width: "100%",
          }}
          labelCol={{ span: 24 }}
        >
          <Controller
            name={"fullname"}
            defaultValue={""}
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <Input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    const key = e.target.value;
                    const isNumber = key.match(/\d+/g);

                    if (key === "") {
                      setFullName("");
                      onChange("");
                      return;
                    }

                    if (!!isNumber) {
                      return;
                    }

                    setFullName(
                      key.replace(
                        // eslint-disable-next-line no-useless-escape
                        /[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi,
                        "",
                      ),
                    );

                    onChange(
                      key.replace(
                        // eslint-disable-next-line no-useless-escape
                        /[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi,
                        "",
                      ),
                    );
                  }}
                  placeholder="Viết họ tên đầy đủ, chữ in hoa không dấu"
                />
              );
            }}
          />
        </Form.Item>
        <p css={SignupPageStyle.textRequired}>
          Viết họ tên đầy đủ, chữ in hoa không dấu
        </p>

        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Số điện thoại</p>
        </div>
        <InputCustomAuth
          placeholder={"Vui lòng nhập số điện thoại"}
          name={"mobile"}
          control={control}
          margin_bottom="12px"
          id={"mobile"}
          errors={errors.mobile?.message}
          validate_status={errors.mobile ? "error" : "success"}
          isPrefix
          onKeyDown={handleInputChange}
          type="number"
        />
        <p css={SignupPageStyle.textRequired}>Vui lòng nhập số điện thoại</p>

        <div css={SignupPageStyle.inputLabel}>
          <p css={SignupPageStyle.textLabel}>Địa chỉ email</p>
        </div>
        <InputCustomAuth
          placeholder={"Vui lòng nhập email"}
          name={"email"}
          control={control}
          margin_bottom="12px"
          id={"email"}
          errors={errors.email?.message}
          validate_status={errors.email ? "error" : "success"}
          isPrefix
        />
        <p css={SignupPageStyle.textRequired}>Nhập chính xác email</p>

        <div css={SignupPageStyle.inputLabel}>
          <StarIcon height={19} width={19} fill="#2d7699" />
          <p css={SignupPageStyle.textLabel}>Mã xác minh</p>
        </div>
        <InputCustomAuth
          onClick={generateRandomNumbers}
          placeholder={"Vui lòng nhập mã xác minh"}
          name={"captcha"}
          control={control}
          margin_bottom="12px"
          id={"captcha"}
          errors={errors.email?.message}
          validate_status={errors.email ? "error" : "success"}
          suffix={
            state.valueCaptcha && (
              <div
                css={SignupPageStyle.blockCaptcha(
                  LIST_BACKGROUND_CAPTCHA[state.numberBg],
                )}
                onClick={generateRandomNumbers}
              >
                <span>{state.valueCaptcha}</span>
              </div>
            )
          }
          onKeyDown={handleInputChange}
          type="number"
        />
        <p css={SignupPageStyle.textRequired}>Nhập chính xác mã xác minh</p>

        <div css={SignupPageStyle.checkBox}>
          <CheckboxCustom
            name={"approve"}
            control={control}
            errors={errors.approve?.message?.toString()}
            validate_status={errors.approve ? "error" : "success"}
          >
            Tôi đã hiểu{" "}
            <span style={{ textDecoration: "underline" }}>
              Thỏa thuận đăng ký người dùng
            </span>
          </CheckboxCustom>
        </div>

        <div css={SignupPageStyle.groupBtn}>
          <Button
            htmlType="submit"
            onClick={() => {
              renderTitle();
              dispatch(setOpenModalNotiMobile(ModalType.NOTI_MOBILE));
            }}
          >
            Đăng ký ngay
          </Button>
          <Button
            onClick={() => {
              navigate("/account/login");
            }}
          >
            Đăng nhập
          </Button>
        </div>
      </Form>
      {!!state.title && <ModalNotiMobile title={state.title} />}
    </div>
  );
};

export default SignupPage;
